import { useState } from "react";
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiFilter, FiSearch } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import FlexOfficeFooter from "../components/FlexOfficeFooter";
import FlexOfficeHeader from "../components/FlexOfficeHeader";
import { richTextToPlainText } from "../../../../utils/richText";

const ViewAllCategories = () => {
    const location = useLocation();
    const { products } = location.state || { products: [] };

    const [filteredProducts, setFilteredProducts] = useState(products);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 15;
    const navigate = useNavigate();

    // Apply filters and sorting
    const applyFilters = (term, category, sort) => {
        let updatedProducts = [...products];

        if (term) {
            updatedProducts = updatedProducts.filter((product) =>
                product.name.toLowerCase().includes(term.toLowerCase())
            );
        }

        if (category) {
            updatedProducts = updatedProducts.filter((product) => product.category === category);
        }

        if (sort) {
            if (sort === "A-Z") {
                updatedProducts.sort((a, b) => a.name.localeCompare(b.name));
            } else if (sort === "Z-A") {
                updatedProducts.sort((a, b) => b.name.localeCompare(a.name));
            } else if (sort === "Size") {
                updatedProducts.sort((a, b) => a.size - b.size);
            }
        }

        setFilteredProducts(updatedProducts);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        applyFilters(term, categoryFilter, sortOrder);
    };

    const handleCategoryChange = (e) => {
        const selectedCategory = e.target.value;
        setCategoryFilter(selectedCategory);
        applyFilters(searchTerm, selectedCategory, sortOrder);
    };

    const handleSortChange = (e) => {
        const selectedSort = e.target.value;
        setSortOrder(selectedSort);
        applyFilters(searchTerm, categoryFilter, selectedSort);
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const handleNext = (primaryId) => {
        navigate('/choice', { state: { primaryId } });
    };

    return (
        <>
            <FlexOfficeHeader />
            <div
                className="py-16 relative fx-m-bg-scroll"
                style={{
                    backgroundImage: `url(${"https://files.oaiusercontent.com/file-TjvySkq3kZtnoNgQMZoF9G?se=2024-12-29T18%3A08%3A27Z&sp=r&sv=2024-08-04&sr=b&rscc=max-age%3D604800%2C%20immutable%2C%20private&rscd=attachment%3B%20filename%3D7f2055f9-91a2-4522-9752-3d9fc4936eed.webp&sig=S9WOLmSHgZLQgFpQxzgiQL5Rsft5zEL2NNTzcWZNPQU%3D"})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundAttachment: "fixed",
                }}
            >
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <div className="fx-m-hide"><br /></div>
                <div className="container mx-auto px-4 mb-8 relative z-10 fx-m-page">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-6">
                        Our Choices
                    </h2>

                    {/* Filters Section */}
                    <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 shadow-lg border border-slate-200 rounded-2xl space-y-4 md:space-y-0">
                        <button
                            onClick={handleBackClick}
                            className="text-slate-600 hover:text-slate-800 transition duration-300 flex items-center font-medium"
                        >
                            <FiArrowLeft className="mr-2" />
                            Back
                        </button>

                        {/* Search Input */}
                        <div className="relative w-full md:w-1/3">
                            <input
                                type="text"
                                placeholder="Search Space..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full p-3 border border-slate-300 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-xl transition-all duration-200"
                            />
                            <FiSearch className="absolute left-3 top-3 text-slate-500" />
                        </div>

                        {/* Category Dropdown */}
                        <div className="relative w-full md:w-1/4">
                            <select
                                value={categoryFilter}
                                onChange={handleCategoryChange}
                                className="w-full p-3 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-xl transition-all duration-200"
                            >
                                <option value="">All Categories</option>
                                <option value="Home">Home</option>
                                <option value="Office">Office</option>
                                <option value="Outdoor">Outdoor</option>
                            </select>
                            <FiFilter className="absolute right-3 top-3 text-slate-500" />
                        </div>

                        {/* Sort Dropdown */}
                        <div className="relative w-full md:w-1/4">
                            <select
                                value={sortOrder}
                                onChange={handleSortChange}
                                className="w-full p-3 border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded-xl transition-all duration-200"
                            >
                                <option value="">Sort By</option>
                                <option value="A-Z">A-Z</option>
                                <option value="Z-A">Z-A</option>
                                <option value="Size">Size</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Products Section */}
                <div className="container mx-auto px-4 relative z-10 fx-m-page">
                    {currentProducts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 fx-m-grid-1">
                            {currentProducts.map((product) => (
                                <div
                                    key={product.primaryId}
                                    className="bg-white p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between rounded-2xl"
                                >
                                    {/* Product Image */}
                                    <img
                                        src={product.imageURL || "https://via.placeholder.com/150"}
                                        alt={product.name}
                                        className="w-full h-48 object-cover rounded-t-2xl mb-4 hover:scale-105 transition-transform duration-300"
                                    />

                                    {/* Product Details */}
                                    <div>
                                        <h3 className="text-xl font-semibold text-slate-800 mb-2">
                                            {product.name}
                                        </h3>
                                        <p className="text-slate-600 mb-4">
                                            {richTextToPlainText(product.description) || "No description available"}
                                        </p>
                                    </div>

                                    {/* CTA Button */}
                                    <button
                                        onClick={() => handleNext(product.primaryId)}
                                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
                                    >
                                        View Details
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <h3 className="text-2xl text-white font-semibold">
                                No products found. Please refine your search or filter.
                            </h3>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div className="container mx-auto px-4 mt-8 flex justify-center relative z-10 space-x-4">
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                        <FiChevronLeft />
                    </button>
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={indexOfLastProduct >= filteredProducts.length}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                        <FiChevronRight />
                    </button>
                </div>
            </div>
            <FlexOfficeFooter />
        </>
    );
};

export default ViewAllCategories;